/**
 * Size chart drawer — open/close, cm/in image panels, Front/Back measure views.
 * Chart measurements are shown as locale-specific images (not converted tables).
 */
class SizeChartController {
  /** @param {HTMLElement} root */
  constructor(root) {
    this.root = root;
    this.dialog = root.querySelector('[data-size-chart-dialog]');
    this.defaultUnit = root.dataset.defaultUnit === 'in' ? 'in' : 'cm';
    this.unit = this.defaultUnit;

    root.querySelector('[data-size-chart-open]')?.addEventListener('click', () => this.open());
    root.querySelector('[data-size-chart-close]')?.addEventListener('click', () => this.close());

    this.dialog?.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });

    this.dialog?.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    });

    root.querySelectorAll('[data-size-chart-unit]').forEach((button) => {
      button.addEventListener('click', () => {
        const next = button.getAttribute('data-size-chart-unit');
        if (next === 'cm' || next === 'in') this.setUnit(next);
      });
    });

    root.querySelectorAll('[data-size-chart-view]').forEach((button) => {
      button.addEventListener('click', () => {
        const view = button.getAttribute('data-size-chart-view');
        if (view === 'front' || view === 'back') this.setMeasureView(view);
      });
    });

    this.setUnit(this.unit, { silent: true });
  }

  open() {
    if (!(this.dialog instanceof HTMLDialogElement)) return;
    if (!this.dialog.open) this.dialog.showModal();
  }

  close() {
    if (!(this.dialog instanceof HTMLDialogElement)) return;
    if (this.dialog.open) this.dialog.close();
  }

  /**
   * @param {'front' | 'back'} view
   */
  setMeasureView(view) {
    this.root.querySelectorAll('[data-size-chart-view]').forEach((button) => {
      const active = button.getAttribute('data-size-chart-view') === view;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    this.root.querySelectorAll('[data-size-chart-view-panel]').forEach((panel) => {
      const active = panel.getAttribute('data-size-chart-view-panel') === view;
      panel.classList.toggle('is-active', active);
      if (active) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });
  }

  /**
   * @param {'cm' | 'in'} unit
   * @param {{ silent?: boolean }} [options]
   */
  setUnit(unit, options = {}) {
    this.unit = unit;

    this.root.querySelectorAll('[data-size-chart-unit]').forEach((button) => {
      const active = button.getAttribute('data-size-chart-unit') === unit;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    this.root.querySelectorAll('[data-size-chart-unit-panel]').forEach((panel) => {
      const active = panel.getAttribute('data-size-chart-unit-panel') === unit;
      panel.classList.toggle('is-active', active);
      if (active) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });

    if (!options.silent) {
      try {
        sessionStorage.setItem('pz-size-chart-unit', unit);
      } catch {
        /* ignore */
      }
    }
  }
}

function initSizeCharts(scope = document) {
  scope.querySelectorAll('[data-size-chart]').forEach((root) => {
    if (!(root instanceof HTMLElement)) return;
    if (root.dataset.sizeChartReady === 'true') return;
    root.dataset.sizeChartReady = 'true';

    let unit = root.dataset.defaultUnit === 'in' ? 'in' : 'cm';
    try {
      const stored = sessionStorage.getItem('pz-size-chart-unit');
      if (stored === 'cm' || stored === 'in') unit = stored;
    } catch {
      /* ignore */
    }

    const hasCm = Boolean(root.querySelector('[data-size-chart-unit-panel="cm"]'));
    const hasIn = Boolean(root.querySelector('[data-size-chart-unit-panel="in"]'));
    if (unit === 'cm' && !hasCm && hasIn) unit = 'in';
    if (unit === 'in' && !hasIn && hasCm) unit = 'cm';

    root.dataset.defaultUnit = unit;

    const controller = new SizeChartController(root);
    controller.setUnit(unit, { silent: true });
  });
}

initSizeCharts();

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) initSizeCharts(event.target);
});

export { SizeChartController, initSizeCharts };
